import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import Navigation from '@/components/Navigation';
import PatientSelector from '@/components/PatientSelector';
import ReactECharts from 'echarts-for-react';
import { jsPDF } from 'jspdf';
import { Download, AlertTriangle, Crown, Activity, Pill, Scale, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { format, subDays, parseISO } from 'date-fns';
import { usePatientContext } from '@/contexts/PatientContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Analytics = ({ user, onLogout }) => {
  const { selectedPatientId, setSelectedPatientId } = usePatientContext();
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [symptoms, setSymptoms] = useState([]);
  const [medications, setMedications] = useState([]);
  const [medicationLogs, setMedicationLogs] = useState([]);
  const [bloodPressure, setBloodPressure] = useState([]);
  const [bloodSugar, setBloodSugar] = useState([]);
  const [weights, setWeights] = useState([]);
  const [moods, setMoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    includeCharts: true,
    includeMedications: true,
    includeSymptoms: true,
    includeNotes: true,
    includeBloodPressure: true,
    includeBloodSugar: true,
    includeWeight: true,
    reportType: 'detailed',
  });
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    fetchPatients();
    fetchSubscriptionStatus();
  }, []);

  useEffect(() => {
    if (selectedPatient) {
      fetchAnalyticsData();
    }
  }, [selectedPatient]);

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const isPremium = () => {
    if (!subscriptionStatus) return false;
    return subscriptionStatus.admin_granted_premium || 
           (subscriptionStatus.plan === 'premium' && 
            (subscriptionStatus.status === 'active' || subscriptionStatus.status === 'trial'));
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setSelectedPatientId(patient.id);
  };

  const fetchPatients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/patients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPatients(response.data);
      
      if (response.data.length > 0) {
        if (selectedPatientId) {
          const storedPatient = response.data.find(p => p.id === selectedPatientId);
          if (storedPatient) {
            setSelectedPatient(storedPatient);
          } else {
            setSelectedPatient(response.data[0]);
            setSelectedPatientId(response.data[0].id);
          }
        } else {
          setSelectedPatient(response.data[0]);
          setSelectedPatientId(response.data[0].id);
        }
      }
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load patients');
      setLoading(false);
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [symptomsRes, medsRes, logsRes, bpRes, bsRes, weightsRes, moodsRes] = await Promise.all([
        axios.get(`${API}/symptoms/${selectedPatient.id}`, { headers }),
        axios.get(`${API}/medications/${selectedPatient.id}`, { headers }),
        axios.get(`${API}/medication-logs/${selectedPatient.id}`, { headers }),
        axios.get(`${API}/blood-pressure/${selectedPatient.id}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/blood-sugar/${selectedPatient.id}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/weights/${selectedPatient.id}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/moods/${selectedPatient.id}`, { headers }).catch(() => ({ data: [] })),
      ]);

      setSymptoms(symptomsRes.data);
      setMedications(medsRes.data);
      setMedicationLogs(logsRes.data);
      setBloodPressure(bpRes.data);
      setBloodSugar(bsRes.data);
      setWeights(weightsRes.data);
      setMoods(moodsRes.data);
      
      checkSymptomAlerts(symptomsRes.data);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    }
  };

  const checkSymptomAlerts = (symptomsData) => {
    const alertsList = [];
    const today = new Date();
    const last7Days = Array.from({ length: 7 }, (_, i) => 
      format(subDays(today, i), 'yyyy-MM-dd')
    );

    const recentSymptoms = symptomsData.filter(s => last7Days.includes(s.date));
    const symptomTracker = {};
    recentSymptoms.sort((a, b) => new Date(b.date) - new Date(a.date));

    recentSymptoms.forEach(entry => {
      entry.symptoms.forEach(symptom => {
        if (!symptomTracker[symptom]) {
          symptomTracker[symptom] = { count: 0, consecutive: 0 };
        }
        symptomTracker[symptom].count++;
      });
    });

    Object.keys(symptomTracker).forEach(symptom => {
      let consecutiveCount = 0;
      for (let i = 0; i < Math.min(7, recentSymptoms.length); i++) {
        const daySymptoms = recentSymptoms[i]?.symptoms || [];
        if (daySymptoms.includes(symptom)) {
          consecutiveCount++;
        } else {
          break;
        }
      }

      if (consecutiveCount >= 3) {
        alertsList.push({
          symptom,
          days: consecutiveCount,
          message: `You've logged ${symptom.toLowerCase()} ${consecutiveCount} days in a row. Consider contacting your doctor.`
        });
      }
    });

    setAlerts(alertsList);
  };

  // Calculate Vitals Stability (Premium Feature)
  const getVitalsStability = () => {
    const bpStability = calculateBPStability();
    const bsStability = calculateBSStability();
    
    return { bp: bpStability, bs: bsStability };
  };

  const calculateBPStability = () => {
    if (bloodPressure.length < 3) return { status: 'insufficient', message: 'Need more readings' };
    
    const recentBP = bloodPressure.slice(0, 7);
    const avgSystolic = recentBP.reduce((sum, r) => sum + r.systolic, 0) / recentBP.length;
    const avgDiastolic = recentBP.reduce((sum, r) => sum + r.diastolic, 0) / recentBP.length;
    
    // Calculate variance
    const systolicVariance = recentBP.reduce((sum, r) => sum + Math.pow(r.systolic - avgSystolic, 2), 0) / recentBP.length;
    const diastolicVariance = recentBP.reduce((sum, r) => sum + Math.pow(r.diastolic - avgDiastolic, 2), 0) / recentBP.length;
    
    const systolicStdDev = Math.sqrt(systolicVariance);
    const diastolicStdDev = Math.sqrt(diastolicVariance);
    
    // Determine stability
    if (systolicStdDev < 10 && diastolicStdDev < 7) {
      return { status: 'stable', message: 'Blood pressure is stable', color: '#4CAF50' };
    } else if (systolicStdDev < 15 && diastolicStdDev < 10) {
      return { status: 'moderate', message: 'Some variation in readings', color: '#FFC107' };
    } else {
      return { status: 'variable', message: 'Significant variation detected', color: '#F44336' };
    }
  };

  const calculateBSStability = () => {
    if (bloodSugar.length < 3) return { status: 'insufficient', message: 'Need more readings' };
    
    const recentBS = bloodSugar.slice(0, 7);
    const avgReading = recentBS.reduce((sum, r) => sum + r.reading, 0) / recentBS.length;
    
    // Calculate variance
    const variance = recentBS.reduce((sum, r) => sum + Math.pow(r.reading - avgReading, 2), 0) / recentBS.length;
    const stdDev = Math.sqrt(variance);
    
    // Determine stability based on coefficient of variation
    const cv = (stdDev / avgReading) * 100;
    
    if (cv < 20) {
      return { status: 'stable', message: 'Blood sugar is stable', color: '#4CAF50' };
    } else if (cv < 35) {
      return { status: 'moderate', message: 'Some variation in readings', color: '#FFC107' };
    } else {
      return { status: 'variable', message: 'Significant variation detected', color: '#F44336' };
    }
  };

  const getSymptomTrendsData = () => {
    if (symptoms.length === 0) return null;

    const symptomCounts = {};
    symptoms.forEach((entry) => {
      entry.symptoms.forEach((symptom) => {
        symptomCounts[symptom] = (symptomCounts[symptom] || 0) + 1;
      });
    });

    return {
      tooltip: { trigger: 'item', formatter: '{b}: {c} times ({d}%)' },
      legend: { orient: 'horizontal', bottom: 10, left: 'center' },
      series: [{
        type: 'pie',
        radius: ['40%', '65%'],
        center: ['50%', '45%'],
        data: Object.entries(symptomCounts).map(([name, value]) => ({ name, value })),
        emphasis: { itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0, 0, 0, 0.5)' } },
        color: ['#A2DAD3', '#F9A7A7', '#A7C7F9', '#F9D6A7', '#C7A7F9'],
      }],
    };
  };

  const getSymptomTimelineData = () => {
    if (symptoms.length === 0) return null;

    const last30Days = Array.from({ length: 30 }, (_, i) => {
      const date = subDays(new Date(), 29 - i);
      return format(date, 'yyyy-MM-dd');
    });

    const uniqueSymptoms = [...new Set(symptoms.flatMap(s => s.symptoms))].slice(0, 5);
    
    const series = uniqueSymptoms.map((symptom, index) => ({
      name: symptom,
      type: 'line',
      smooth: true,
      data: last30Days.map(date => {
        const dayData = symptoms.find(s => s.date === date);
        return dayData?.symptoms.includes(symptom) ? 1 : 0;
      }),
      color: ['#A2DAD3', '#F9A7A7', '#A7C7F9', '#F9D6A7', '#C7A7F9'][index],
    }));

    return {
      tooltip: { trigger: 'axis' },
      legend: { data: uniqueSymptoms, bottom: 0 },
      grid: { left: '3%', right: '4%', bottom: '15%', containLabel: true },
      xAxis: {
        type: 'category',
        data: last30Days.map(d => format(parseISO(d), 'MMM d')),
        axisLabel: { interval: 4, rotate: 45 }
      },
      yAxis: { type: 'value', max: 1, axisLabel: { formatter: (val) => val === 1 ? 'Yes' : '' } },
      series,
    };
  };

  const getWeightChartData = () => {
    if (weights.length === 0) return null;

    const sortedWeights = [...weights].sort((a, b) => new Date(a.date) - new Date(b.date)).slice(-30);
    
    return {
      tooltip: { trigger: 'axis', formatter: (params) => `${params[0].axisValue}<br/>Weight: ${params[0].value} ${sortedWeights[0]?.unit || 'kg'}` },
      grid: { left: '3%', right: '4%', bottom: '10%', containLabel: true },
      xAxis: {
        type: 'category',
        data: sortedWeights.map(w => format(parseISO(w.date), 'MMM d')),
        axisLabel: { rotate: 45 }
      },
      yAxis: { 
        type: 'value',
        name: sortedWeights[0]?.unit || 'kg',
        min: (value) => Math.floor(value.min - 2),
        max: (value) => Math.ceil(value.max + 2)
      },
      series: [{
        data: sortedWeights.map(w => w.weight),
        type: 'line',
        smooth: true,
        areaStyle: { color: 'rgba(162, 218, 211, 0.3)' },
        lineStyle: { color: '#A2DAD3', width: 3 },
        itemStyle: { color: '#A2DAD3' },
      }],
    };
  };

  const getMoodChartData = () => {
    if (moods.length === 0) return null;

    const moodValues = { 'great': 5, 'good': 4, 'okay': 3, 'bad': 2, 'awful': 1 };
    const moodEmojis = { 'great': '😄', 'good': '🙂', 'okay': '😐', 'bad': '😔', 'awful': '😢' };
    
    const sortedMoods = [...moods].sort((a, b) => new Date(a.date) - new Date(b.date)).slice(-14);
    
    return {
      tooltip: { 
        trigger: 'axis',
        formatter: (params) => `${params[0].axisValue}<br/>Mood: ${moodEmojis[sortedMoods.find(m => format(parseISO(m.date), 'MMM d') === params[0].axisValue)?.mood] || ''}`
      },
      grid: { left: '3%', right: '4%', bottom: '10%', containLabel: true },
      xAxis: {
        type: 'category',
        data: sortedMoods.map(m => format(parseISO(m.date), 'MMM d')),
      },
      yAxis: { 
        type: 'value',
        min: 1,
        max: 5,
        axisLabel: { formatter: (val) => ['', '😢', '😔', '😐', '🙂', '😄'][val] || '' }
      },
      series: [{
        data: sortedMoods.map(m => moodValues[m.mood] || 3),
        type: 'line',
        smooth: true,
        lineStyle: { color: '#F9A7A7', width: 3 },
        itemStyle: { color: '#F9A7A7' },
      }],
    };
  };

  const exportToPDF = async () => {
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      
      // Header
      pdf.setFillColor(162, 218, 211);
      pdf.rect(0, 0, pageWidth, 40, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.text('Health Report', 20, 25);
      pdf.setFontSize(12);
      pdf.text(`Patient: ${selectedPatient.name}`, 20, 35);

      // Date and generated info
      pdf.setTextColor(100, 100, 100);
      pdf.setFontSize(10);
      pdf.text(`Generated: ${format(new Date(), 'MMMM d, yyyy')}`, pageWidth - 60, 25);
      
      let yPosition = 55;

      // Medications List
      if (exportOptions.includeMedications && medications.length > 0) {
        pdf.setTextColor(162, 218, 211);
        pdf.setFontSize(14);
        pdf.text('Current Medications', 20, yPosition);
        yPosition += 8;
        
        pdf.setTextColor(60, 60, 60);
        pdf.setFontSize(10);
        medications.forEach(med => {
          pdf.text(`• ${med.name} - ${med.dosage} (${med.frequency})`, 25, yPosition);
          if (med.purpose) {
            pdf.setTextColor(120, 120, 120);
            pdf.text(`  For: ${med.purpose}`, 30, yPosition + 4);
            pdf.setTextColor(60, 60, 60);
            yPosition += 4;
          }
          yPosition += 6;
        });
        yPosition += 5;
      }

      // Blood Pressure
      if (exportOptions.includeBloodPressure && bloodPressure.length > 0) {
        pdf.setTextColor(162, 218, 211);
        pdf.setFontSize(14);
        pdf.text('Blood Pressure Records (Last 10)', 20, yPosition);
        yPosition += 8;
        
        pdf.setTextColor(60, 60, 60);
        pdf.setFontSize(10);
        bloodPressure.slice(0, 10).forEach(record => {
          pdf.text(`${record.date} ${record.time}: ${record.systolic}/${record.diastolic} mmHg`, 25, yPosition);
          yPosition += 5;
        });
        yPosition += 5;
      }

      // Blood Sugar
      if (exportOptions.includeBloodSugar && bloodSugar.length > 0) {
        if (yPosition > 240) {
          pdf.addPage();
          yPosition = 20;
        }
        
        pdf.setTextColor(162, 218, 211);
        pdf.setFontSize(14);
        pdf.text('Blood Sugar Records (Last 10)', 20, yPosition);
        yPosition += 8;
        
        pdf.setTextColor(60, 60, 60);
        pdf.setFontSize(10);
        bloodSugar.slice(0, 10).forEach(record => {
          pdf.text(`${record.date} ${record.time}: ${record.reading} mg/dL ${record.meal_context ? `(${record.meal_context})` : ''}`, 25, yPosition);
          yPosition += 5;
        });
        yPosition += 5;
      }

      // Weight
      if (exportOptions.includeWeight && weights.length > 0) {
        if (yPosition > 240) {
          pdf.addPage();
          yPosition = 20;
        }
        
        pdf.setTextColor(162, 218, 211);
        pdf.setFontSize(14);
        pdf.text('Weight Records (Last 10)', 20, yPosition);
        yPosition += 8;
        
        pdf.setTextColor(60, 60, 60);
        pdf.setFontSize(10);
        weights.slice(0, 10).forEach(record => {
          pdf.text(`${record.date}: ${record.weight} ${record.unit}`, 25, yPosition);
          yPosition += 5;
        });
      }

      // Footer
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setDrawColor(162, 218, 211);
        pdf.setLineWidth(0.5);
        pdf.line(20, 280, pageWidth - 20, 280);
        pdf.setFontSize(9);
        pdf.setTextColor(162, 218, 211);
        pdf.text('Annie Cares - Made for caregivers', pageWidth / 2, 287, { align: 'center' });
      }

      pdf.save(`${selectedPatient.name}-health-report-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
      toast.success('PDF exported successfully');
      setShowExportDialog(false);
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  if (loading || !selectedPatient) {
    return (
      <div className="min-h-screen p-4">
        <Navigation active="analytics" />
        <div className="max-w-4xl mx-auto pt-8 text-center">
          <h2 className="text-2xl font-semibold mb-4">Loading...</h2>
        </div>
      </div>
    );
  }

  const symptomChartData = getSymptomTrendsData();
  const timelineChartData = getSymptomTimelineData();
  const weightChartData = getWeightChartData();
  const moodChartData = getMoodChartData();
  const vitalsStability = getVitalsStability();

  return (
    <div className="min-h-screen pb-24 md:pb-8" data-testid="analytics-page">
      <Navigation active="analytics" />

      <div className="max-w-6xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold" style={{ color: '#333' }}>Analytics</h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">Health insights and trends</p>
          </motion.div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <button
              data-testid="export-pdf-button"
              onClick={() => setShowExportDialog(true)}
              className="btn btn-coral flex items-center gap-2 text-sm sm:text-base"
            >
              <Download size={16} />
              <span className="hidden sm:inline">Export</span> PDF
            </button>
            <PatientSelector
              patients={patients}
              selectedPatient={selectedPatient}
              onSelectPatient={handlePatientSelect}
              onPatientsChange={fetchPatients}
            />
          </div>
        </div>

        {/* Alert Highlights */}
        {alerts.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
            {alerts.map((alert, index) => (
              <div
                key={index}
                className="p-4 rounded-xl mb-3 flex items-start gap-3"
                style={{ backgroundColor: '#fff5f5', border: '2px solid #F9A7A7' }}
                data-testid="symptom-alert"
              >
                <AlertTriangle size={20} style={{ color: '#F9A7A7', flexShrink: 0, marginTop: '2px' }} />
                <p className="text-sm" style={{ color: '#333' }}>{alert.message}</p>
              </div>
            ))}
          </motion.div>
        )}

        <div id="analytics-content">
          {/* Summary Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6"
          >
            <div className="card p-3 sm:p-4" style={{ backgroundColor: '#f0f9f8' }}>
              <p className="text-xs sm:text-sm text-gray-600">Symptoms Logged</p>
              <p className="text-2xl sm:text-3xl font-bold mt-1 sm:mt-2" style={{ color: '#A2DAD3' }}>{symptoms.length}</p>
            </div>
            <div className="card p-3 sm:p-4" style={{ backgroundColor: '#fff5f5' }}>
              <p className="text-xs sm:text-sm text-gray-600">Medications</p>
              <p className="text-2xl sm:text-3xl font-bold mt-1 sm:mt-2" style={{ color: '#F9A7A7' }}>{medications.length}</p>
            </div>
            <div className="card p-3 sm:p-4" style={{ backgroundColor: '#f0f5f9' }}>
              <p className="text-xs sm:text-sm text-gray-600">BP Readings</p>
              <p className="text-2xl sm:text-3xl font-bold mt-1 sm:mt-2" style={{ color: '#A7C7F9' }}>{bloodPressure.length}</p>
            </div>
            <div className="card p-3 sm:p-4" style={{ backgroundColor: '#f9f5f0' }}>
              <p className="text-xs sm:text-sm text-gray-600">Weight Logs</p>
              <p className="text-2xl sm:text-3xl font-bold mt-1 sm:mt-2" style={{ color: '#F9D6A7' }}>{weights.length}</p>
            </div>
          </motion.div>

          {/* Vitals Stability Indicator - Premium Feature */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card mb-6"
            style={!isPremium() ? { opacity: 0.7 } : {}}
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2">
                <Activity size={20} className="sm:w-6 sm:h-6" style={{ color: '#A2DAD3' }} />
                <h2 className="text-lg sm:text-xl font-semibold" style={{ color: '#A2DAD3' }}>Vitals Stability</h2>
              </div>
              {!isPremium() && (
                <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium w-fit" style={{ backgroundColor: '#f0f9f8', color: '#A2DAD3' }}>
                  <Crown size={12} />
                  Premium
                </span>
              )}
            </div>
            
            {isPremium() ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Blood Pressure Stability */}
                <div className="p-4 rounded-xl" style={{ backgroundColor: '#f8f8f8' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">Blood Pressure</span>
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium"
                      style={{ backgroundColor: `${vitalsStability.bp.color}20`, color: vitalsStability.bp.color }}
                    >
                      {vitalsStability.bp.status === 'stable' ? 'Stable' : 
                       vitalsStability.bp.status === 'moderate' ? 'Moderate' : 
                       vitalsStability.bp.status === 'variable' ? 'Variable' : 'N/A'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{vitalsStability.bp.message}</p>
                  {bloodPressure.length > 0 && (
                    <p className="text-xs text-gray-500 mt-2">
                      Latest: {bloodPressure[0].systolic}/{bloodPressure[0].diastolic} mmHg
                    </p>
                  )}
                </div>

                {/* Blood Sugar Stability */}
                <div className="p-4 rounded-xl" style={{ backgroundColor: '#f8f8f8' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">Blood Sugar</span>
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium"
                      style={{ backgroundColor: `${vitalsStability.bs.color}20`, color: vitalsStability.bs.color }}
                    >
                      {vitalsStability.bs.status === 'stable' ? 'Stable' : 
                       vitalsStability.bs.status === 'moderate' ? 'Moderate' : 
                       vitalsStability.bs.status === 'variable' ? 'Variable' : 'N/A'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{vitalsStability.bs.message}</p>
                  {bloodSugar.length > 0 && (
                    <p className="text-xs text-gray-500 mt-2">
                      Latest: {bloodSugar[0].reading} mg/dL
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-gray-500 mb-3">Upgrade to Premium to see your vitals stability analysis</p>
                <a href="/subscription" className="btn btn-primary inline-flex items-center gap-2">
                  <Crown size={16} />
                  Upgrade Now
                </a>
              </div>
            )}
          </motion.div>

          {/* Medications List */}
          {medications.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="card mb-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <Pill size={24} style={{ color: '#F9A7A7' }} />
                <h2 className="text-xl font-semibold" style={{ color: '#F9A7A7' }}>Current Medications</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {medications.map(med => (
                  <div key={med.id} className="p-3 rounded-xl" style={{ backgroundColor: '#fff5f5' }}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{med.name}</span>
                      <span className="text-sm text-gray-600">{med.dosage}</span>
                    </div>
                    {med.purpose && (
                      <p className="text-sm text-gray-500 mt-1">For: {med.purpose}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">{med.frequency}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Symptom Distribution */}
            {symptomChartData && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="card"
                data-testid="symptom-chart"
              >
                <h2 className="text-xl font-semibold mb-4" style={{ color: '#A2DAD3' }}>Symptom Distribution</h2>
                <ReactECharts option={symptomChartData} style={{ height: '300px' }} />
              </motion.div>
            )}

            {/* Weight Trend */}
            {weightChartData && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="card"
                data-testid="weight-chart"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Scale size={20} style={{ color: '#A2DAD3' }} />
                  <h2 className="text-xl font-semibold" style={{ color: '#A2DAD3' }}>Weight Trend</h2>
                </div>
                <ReactECharts option={weightChartData} style={{ height: '300px' }} />
              </motion.div>
            )}

            {/* Mood Trend */}
            {moodChartData && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="card"
                data-testid="mood-chart"
              >
                <h2 className="text-xl font-semibold mb-4" style={{ color: '#F9A7A7' }}>Mood Trend (Last 14 Days)</h2>
                <ReactECharts option={moodChartData} style={{ height: '300px' }} />
              </motion.div>
            )}

            {/* Symptom Timeline */}
            {timelineChartData && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="card lg:col-span-2"
                data-testid="timeline-chart"
              >
                <h2 className="text-xl font-semibold mb-4" style={{ color: '#A2DAD3' }}>Symptom Timeline (Last 30 Days)</h2>
                <ReactECharts option={timelineChartData} style={{ height: '300px' }} />
              </motion.div>
            )}
          </div>

          {symptoms.length === 0 && medications.length === 0 && weights.length === 0 && (
            <div className="card text-center py-12">
              <p className="text-gray-500">No data available yet. Start tracking to see insights.</p>
            </div>
          )}

          {/* Footer */}
          <div className="app-footer">
            Made with care for caregivers
          </div>
        </div>
      </div>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Customize Export</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 px-2" data-testid="export-options">
            <div>
              <label className="block text-sm font-medium mb-3">Include in Export</label>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="symptoms"
                    checked={exportOptions.includeSymptoms}
                    onCheckedChange={(checked) => setExportOptions({ ...exportOptions, includeSymptoms: checked })}
                  />
                  <label htmlFor="symptoms" className="text-sm cursor-pointer flex-1">Symptoms</label>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="medications"
                    checked={exportOptions.includeMedications}
                    onCheckedChange={(checked) => setExportOptions({ ...exportOptions, includeMedications: checked })}
                  />
                  <label htmlFor="medications" className="text-sm cursor-pointer flex-1">Medications</label>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="bloodPressure"
                    checked={exportOptions.includeBloodPressure}
                    onCheckedChange={(checked) => setExportOptions({ ...exportOptions, includeBloodPressure: checked })}
                  />
                  <label htmlFor="bloodPressure" className="text-sm cursor-pointer flex-1">Blood Pressure</label>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="bloodSugar"
                    checked={exportOptions.includeBloodSugar}
                    onCheckedChange={(checked) => setExportOptions({ ...exportOptions, includeBloodSugar: checked })}
                  />
                  <label htmlFor="bloodSugar" className="text-sm cursor-pointer flex-1">Blood Sugar</label>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="weight"
                    checked={exportOptions.includeWeight}
                    onCheckedChange={(checked) => setExportOptions({ ...exportOptions, includeWeight: checked })}
                  />
                  <label htmlFor="weight" className="text-sm cursor-pointer flex-1">Weight</label>
                </div>
              </div>
            </div>

            <button
              data-testid="confirm-export-button"
              onClick={exportToPDF}
              className="btn btn-primary w-full"
            >
              Generate PDF
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Analytics;
